import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
    ArrowLeft,
    Palette,
    Gauge,
    UsersThree,
    ToggleLeft as Toggle,
    Plus,
    UserSwitch,
    Copy,
    Warning,
    CheckCircle,
    X,
} from "@phosphor-icons/react";

const LIMIT_KEYS = [
    { key: "candidates", label: "Candidates" },
    { key: "jobs", label: "Requirements" },
    { key: "clients", label: "Clients" },
    { key: "ai_matches", label: "AI matches" },
    { key: "seats", label: "Team seats" },
];

const fmtLimit = (v) => v === null || v === undefined ? "plan default" : v === -1 ? "∞" : v;

const TABS = [
    { key: "config",   label: "Configuration", icon: Palette },
    { key: "users",    label: "Users",         icon: UsersThree },
    { key: "features", label: "Features",      icon: Toggle },
    { key: "limits",   label: "Limits",        icon: Gauge },
];

export default function SuperAdminWorkspaceDetail() {
    const { id } = useParams();
    const [ws, setWs] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState("config");

    const reload = async () => {
        setLoading(true);
        try {
            // We use the list endpoint filtered client-side (small dataset).
            const r = await api.get("/workspaces");
            const found = (r.data.items || []).find((w) => w.id === id);
            if (!found) { toast.error("Workspace not found"); setWs(null); }
            else setWs(found);
        } catch (err) {
            toast.error("Failed to load workspace");
        } finally { setLoading(false); }
    };
    useEffect(() => { reload(); }, [id]);

    if (loading) return <div className="max-w-6xl mx-auto p-6 text-sm text-[#A3A3A3]">Loading workspace…</div>;
    if (!ws) return (
        <div className="max-w-6xl mx-auto p-6">
            <p className="text-sm text-[#525252]">Workspace not found.</p>
        </div>
    );

    return (
        <div className="w-full min-w-0 max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-8" data-testid="superadmin-ws-detail">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-6">
                <div>
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-cobalt mb-1">
                        {ws.slug} · {ws.plan}
                    </p>
                    <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tight">{ws.name}</h1>
                    <p className="text-sm text-[#525252] mt-1">
                        {ws.counts?.users || 0} users · {ws.counts?.candidates || 0} candidates · {ws.counts?.jobs || 0} jobs
                    </p>
                </div>
            </div>

            <div className="border-b border-[#E5E5E5] flex items-center gap-1 mb-6 overflow-x-auto">
                {TABS.map((t) => (
                    <button key={t.key}
                            onClick={() => setTab(t.key)}
                            data-testid={`superadmin-tab-${t.key}`}
                            className={`text-xs font-black uppercase tracking-wider px-4 py-2.5 inline-flex items-center gap-1.5 border-b-2 transition-colors whitespace-nowrap ${
                                tab === t.key ? "border-ink text-ink" : "border-transparent text-[#A3A3A3] hover:text-ink"
                            }`}>
                        <t.icon size={12} weight="bold" /> {t.label}
                    </button>
                ))}
            </div>

            {tab === "config"   && <ConfigTab ws={ws} onSaved={reload} />}
            {tab === "users"    && <UsersTab ws={ws} onChanged={reload} />}
            {tab === "features" && <FeaturesTab ws={ws} onSaved={reload} />}
            {tab === "limits"   && <LimitsTab ws={ws} onSaved={reload} />}
        </div>
    );
}

// ---------- CONFIG TAB (branding + core plan/subdomain) ----------
function ConfigTab({ ws, onSaved }) {
    const [core, setCore] = useState({
        name: ws.name || "",
        plan: ws.plan || "free",
        subdomain: ws.subdomain || "",
    });
    const [settings, setSettings] = useState({
        logo_url: ws.settings?.logo_url || "",
        primary_color: ws.settings?.primary_color || "#0A0A0A",
        brand_font: ws.settings?.brand_font || "Space Grotesk",
    });
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e?.preventDefault();
        setSaving(true);
        try {
            const promises = [];
            const coreBody = {};
            if (core.name !== ws.name) coreBody.name = core.name.trim();
            if (core.plan !== ws.plan) coreBody.plan = core.plan;
            if (core.subdomain !== (ws.subdomain || "")) coreBody.subdomain = core.subdomain.trim().toLowerCase();
            if (Object.keys(coreBody).length) promises.push(api.patch(`/workspaces/${ws.id}`, coreBody));

            const settingsBody = {};
            if (settings.logo_url) settingsBody.logo_url = settings.logo_url.trim();
            if (settings.primary_color && /^#[0-9A-Fa-f]{6}$/.test(settings.primary_color)) settingsBody.primary_color = settings.primary_color;
            if (settings.brand_font) settingsBody.brand_font = settings.brand_font.trim();
            if (Object.keys(settingsBody).length) promises.push(api.patch(`/workspaces/${ws.id}/settings`, settingsBody));

            if (!promises.length) { toast.info("Nothing to save."); return; }
            await Promise.all(promises);
            toast.success(`"${ws.name}" updated.`);
            // Plan or subdomain may have changed → refresh feature flags across the app.
            window.dispatchEvent(new CustomEvent("naukri:features-changed"));
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={save} data-testid="superadmin-config-form" className="space-y-8 max-w-3xl">
            {/* Core */}
            <section>
                <p className="text-[10px] font-black uppercase tracking-wider text-ink mb-3">Core</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Name" value={core.name} onChange={(v) => setCore({ ...core, name: v })}
                           testid="config-name" />
                    <Field label="Subdomain (acme.naukridaddy.com)" value={core.subdomain}
                           onChange={(v) => setCore({ ...core, subdomain: v.toLowerCase() })}
                           testid="config-subdomain" mono placeholder="acme" />
                    <div>
                        <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">Plan</label>
                        <div className="grid grid-cols-3 gap-2">
                            {["free", "pro", "enterprise"].map((p) => (
                                <button type="button" key={p}
                                        onClick={() => setCore({ ...core, plan: p })}
                                        data-testid={`config-plan-${p}`}
                                        className={`text-xs font-black uppercase tracking-wider px-3 py-2 border transition-colors ${
                                            core.plan === p ? "bg-ink text-white border-ink" : "border-[#E5E5E5] hover:border-ink"
                                        }`}>
                                    {p}
                                </button>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* Branding */}
            <section>
                <p className="text-[10px] font-black uppercase tracking-wider text-ink mb-3 flex items-center gap-1.5">
                    <Palette size={12} weight="bold" /> Branding
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Logo URL" testid="config-logo-url" value={settings.logo_url}
                           onChange={(v) => setSettings({ ...settings, logo_url: v })}
                           placeholder="https://example.com/logo.png" />
                    <Field label="Primary color (#hex)" testid="config-primary-color" value={settings.primary_color}
                           onChange={(v) => setSettings({ ...settings, primary_color: v })}
                           placeholder="#0A0A0A" mono />
                    <Field label="Brand font" testid="config-brand-font" value={settings.brand_font}
                           onChange={(v) => setSettings({ ...settings, brand_font: v })}
                           placeholder="Space Grotesk" />
                </div>
                {settings.logo_url && (
                    <div className="mt-3 flex items-center gap-3 border border-[#E5E5E5] p-3">
                        <span className="text-[10px] font-black uppercase text-[#A3A3A3]">Preview:</span>
                        <img src={settings.logo_url} alt="Logo preview" className="h-6 w-auto"
                             onError={(e) => { e.currentTarget.style.opacity = 0.3; }} />
                        <span data-testid="config-color-swatch" className="h-6 w-6 border border-[#E5E5E5]"
                              style={{ backgroundColor: settings.primary_color }} />
                    </div>
                )}
            </section>

            <div className="flex justify-end border-t border-[#F5F5F5] pt-4">
                <button type="submit" disabled={saving} data-testid="config-save-btn"
                        className="btn-gradient px-5 py-2.5 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2 disabled:opacity-60">
                    {saving ? "Saving…" : "Save configuration"}
                </button>
            </div>
        </form>
    );
}

// ---------- USERS TAB (list + create + impersonate) ----------
function UsersTab({ ws, onChanged }) {
    const { setUser } = useAuth();
    const nav = useNavigate();
    const [users, setUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showCreate, setShowCreate] = useState(false);
    const [invite, setInvite] = useState({ name: "", email: "", role: "recruiter" });
    const [creating, setCreating] = useState(false);
    const [created, setCreated] = useState(null); // {user, temporary_password}

    const load = async () => {
        setLoading(true);
        try {
            const r = await api.get(`/superadmin/workspaces/${ws.id}/users`);
            setUsers(r.data.items || []);
        } catch { toast.error("Failed to load users"); }
        finally { setLoading(false); }
    };
    useEffect(() => { load(); }, [ws.id]);

    const create = async (e) => {
        e?.preventDefault();
        setCreating(true);
        try {
            const r = await api.post("/superadmin/users", {
                workspace_id: ws.id,
                name: invite.name.trim(),
                email: invite.email.trim().toLowerCase(),
                role: invite.role,
            });
            setCreated({ user: r.data.user, password: r.data.temporary_password });
            setInvite({ name: "", email: "", role: "recruiter" });
            setShowCreate(false);
            load();
            onChanged();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Couldn't create user");
        } finally { setCreating(false); }
    };

    const impersonate = async (u) => {
        // NOTE: previously used window.confirm() which is silently blocked
        // inside iframed previews on some mobile browsers, making the button
        // appear inert. We now use a sonner-style two-step confirm that works
        // reliably everywhere. Impersonation is fully reversible (exit banner).
        const doImpersonate = async () => {
            try {
                const r = await api.post("/superadmin/impersonate", {
                    user_id: u.id,
                    reason: `Support session from superadmin console`,
                });
                const p = r.data.impersonating;
                const detail = { email: p.email, role: p.role, workspace_id: p.workspace_id };
                // Persist BEFORE navigate so banner reads it on mount.
                localStorage.setItem("naukri:impersonation", JSON.stringify(detail));
                window.dispatchEvent(new CustomEvent("naukri:impersonation", { detail }));
                // Refresh /auth/me to reload as the target user
                const me = await api.get("/auth/me");
                setUser(me.data);
                toast.success(`Now impersonating ${p.email}`);
                nav(p.role === "candidate" ? "/candidate" : "/dashboard");
            } catch (err) {
                toast.error(err?.response?.data?.detail || "Couldn't start impersonation");
            }
        };
        toast(`Login as ${u.email}?`, {
            description: `You'll act as this user for 30 minutes. Exit any time from the top banner.`,
            action: { label: "Login as", onClick: doImpersonate },
            cancel: { label: "Cancel", onClick: () => {} },
            duration: 10000,
        });
    };

    const toggleActive = async (u) => {
        try {
            await api.patch(`/superadmin/users/${u.id}/active`, { active: !u.active });
            toast.success(u.active ? `${u.email} suspended` : `${u.email} reactivated`);
            load();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed");
        }
    };

    return (
        <div className="space-y-4" data-testid="superadmin-users-tab">
            <div className="flex justify-between items-center">
                <p className="text-sm text-[#525252]">
                    {users.length} user{users.length === 1 ? "" : "s"} in this workspace
                </p>
                <button onClick={() => setShowCreate(true)} data-testid="superadmin-invite-btn"
                        className="btn-gradient px-4 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center gap-2">
                    <Plus size={12} weight="bold" /> Create user
                </button>
            </div>

            {loading && <p className="text-sm text-[#A3A3A3]">Loading…</p>}
            {!loading && (
                <>
                {/* Mobile cards */}
                <div className="sm:hidden space-y-3">
                    {users.map((u) => (
                        <div key={`m-${u.id}`} data-testid={`superadmin-user-card-${u.id}`} className="bg-white border border-[#E5E5E5] p-4">
                            <div className="flex items-start justify-between gap-2 mb-2">
                                <div className="min-w-0">
                                    <p className="font-heading font-bold truncate">{u.name || "—"}</p>
                                    <p className="text-xs text-[#525252] truncate">{u.email}</p>
                                </div>
                                <div className="flex flex-col items-end gap-1 shrink-0">
                                    <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-[#F5F5F5] text-[#525252]">
                                        {u.role}
                                    </span>
                                    {u.active === false ? (
                                        <span className="text-[10px] font-black uppercase text-[#DC2626]">Suspended</span>
                                    ) : (
                                        <span className="text-[10px] font-black uppercase text-emerald-600">Active</span>
                                    )}
                                </div>
                            </div>
                            <div className="pt-2 border-t border-[#F5F5F5] flex items-center gap-3">
                                <button onClick={() => impersonate(u)} data-testid={`superadmin-impersonate-card-${u.id}`}
                                        className="text-cobalt hover:text-ink inline-flex items-center gap-1 text-xs font-black uppercase">
                                    <UserSwitch size={12} weight="bold" /> Login as
                                </button>
                                <button onClick={() => toggleActive(u)}
                                        className="text-[#525252] hover:text-ink text-xs font-black uppercase">
                                    {u.active === false ? "Reactivate" : "Suspend"}
                                </button>
                            </div>
                        </div>
                    ))}
                    {users.length === 0 && (
                        <div className="bg-white border border-[#E5E5E5] p-8 text-center text-sm text-[#A3A3A3]">
                            No users yet — create the first admin for this workspace.
                        </div>
                    )}
                </div>

                {/* Desktop table */}
                <div className="hidden sm:block bg-white border border-[#E5E5E5] overflow-x-auto">
                    <table className="w-full text-sm min-w-[680px]">
                        <thead className="bg-[#FAFAFA] border-b border-[#E5E5E5]">
                            <tr className="text-left">
                                {["Name", "Email", "Role", "Status", ""].map((h) => (
                                    <th key={h} className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-[#525252]">{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {users.map((u) => (
                                <tr key={u.id} data-testid={`superadmin-user-${u.id}`} className="border-b border-[#F5F5F5]">
                                    <td className="px-4 py-3 font-heading font-bold">{u.name || "—"}</td>
                                    <td className="px-4 py-3 text-xs">{u.email}</td>
                                    <td className="px-4 py-3">
                                        <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 bg-[#F5F5F5] text-[#525252]">
                                            {u.role}
                                        </span>
                                    </td>
                                    <td className="px-4 py-3">
                                        {u.active === false ? (
                                            <span className="text-[10px] font-black uppercase text-[#DC2626]">Suspended</span>
                                        ) : (
                                            <span className="text-[10px] font-black uppercase text-emerald-600">Active</span>
                                        )}
                                    </td>
                                    <td className="px-4 py-3 flex items-center gap-2">
                                        <button onClick={() => impersonate(u)} data-testid={`superadmin-impersonate-${u.id}`}
                                                className="text-cobalt hover:text-ink inline-flex items-center gap-1 text-xs font-black uppercase">
                                            <UserSwitch size={12} weight="bold" /> Login as
                                        </button>
                                        <button onClick={() => toggleActive(u)}
                                                className="text-[#525252] hover:text-ink text-xs font-black uppercase">
                                            {u.active === false ? "Reactivate" : "Suspend"}
                                        </button>
                                    </td>
                                </tr>
                            ))}
                            {users.length === 0 && (
                                <tr><td colSpan="5" className="px-4 py-8 text-center text-sm text-[#A3A3A3]">
                                    No users yet — create the first admin for this workspace.
                                </td></tr>
                            )}
                        </tbody>
                    </table>
                </div>
                </>
            )}

            {/* Create user modal */}
            {showCreate && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setShowCreate(false)}>
                    <div className="bg-white w-full max-w-md p-6 relative" onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setShowCreate(false)} className="absolute top-4 right-4 text-[#A3A3A3] hover:text-ink">
                            <X size={16} weight="bold" />
                        </button>
                        <h2 className="font-heading text-xl font-black tracking-tight mb-1">Create user</h2>
                        <p className="text-xs text-[#525252] mb-4">Provisions a login for <b>{ws.name}</b>. Temporary password shown once.</p>
                        <form onSubmit={create} data-testid="invite-form" className="space-y-3">
                            <Field label="Full name" value={invite.name} testid="invite-name"
                                   onChange={(v) => setInvite({ ...invite, name: v })} placeholder="Priya Sharma" />
                            <Field label="Email" value={invite.email} testid="invite-email"
                                   onChange={(v) => setInvite({ ...invite, email: v })} placeholder="priya@acme.com" />
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">Role</label>
                                <div className="grid grid-cols-3 gap-2">
                                    {["admin", "recruiter", "hiring_manager"].map((r) => (
                                        <button type="button" key={r}
                                                onClick={() => setInvite({ ...invite, role: r })}
                                                data-testid={`invite-role-${r}`}
                                                className={`text-[10px] font-black uppercase tracking-wider px-2 py-2 border ${
                                                    invite.role === r ? "bg-ink text-white border-ink" : "border-[#E5E5E5] hover:border-ink"
                                                }`}>
                                            {r === "hiring_manager" ? "HM" : r}
                                        </button>
                                    ))}
                                </div>
                            </div>
                            <div className="flex justify-end gap-2 pt-2">
                                <button type="button" onClick={() => setShowCreate(false)}
                                        className="px-4 py-2 text-xs font-black uppercase tracking-wider text-[#525252]">
                                    Cancel
                                </button>
                                <button type="submit" disabled={creating} data-testid="invite-submit"
                                        className="btn-gradient px-4 py-2 text-xs font-black uppercase tracking-wider disabled:opacity-60">
                                    {creating ? "Creating…" : "Create user"}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Show generated password once */}
            {created && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setCreated(null)}>
                    <div className="bg-white w-full max-w-md p-6 relative" data-testid="invite-success-modal"
                         onClick={(e) => e.stopPropagation()}>
                        <button onClick={() => setCreated(null)} className="absolute top-4 right-4 text-[#A3A3A3] hover:text-ink">
                            <X size={16} weight="bold" />
                        </button>
                        <CheckCircle size={28} weight="fill" className="text-emerald-600 mb-2" />
                        <h2 className="font-heading text-xl font-black mb-1">User created</h2>
                        <p className="text-xs text-[#525252] mb-4">Share these credentials over a secure channel. This password will not be shown again.</p>
                        <div className="bg-[#FAFAFA] border border-[#E5E5E5] p-3 space-y-2 text-sm font-mono">
                            <div>
                                <p className="text-[10px] uppercase text-[#A3A3A3] mb-0.5">Email</p>
                                <p data-testid="invite-shown-email">{created.user.email}</p>
                            </div>
                            <div>
                                <p className="text-[10px] uppercase text-[#A3A3A3] mb-0.5 flex items-center justify-between">
                                    Temporary password
                                    <button
                                        onClick={() => { navigator.clipboard.writeText(created.password); toast.success("Copied"); }}
                                        className="text-cobalt inline-flex items-center gap-1 normal-case"
                                    >
                                        <Copy size={10} /> Copy
                                    </button>
                                </p>
                                <p data-testid="invite-shown-password">{created.password}</p>
                            </div>
                        </div>
                        <p className="text-[10px] text-[#A3A3A3] mt-3 inline-flex items-start gap-1">
                            <Warning size={10} weight="fill" className="mt-0.5 flex-shrink-0" />
                            User will be forced to reset on first login.
                        </p>
                    </div>
                </div>
            )}
        </div>
    );
}

// ---------- FEATURES TAB (per-tenant overrides) ----------
function FeaturesTab({ ws, onSaved }) {
    const [catalog, setCatalog] = useState(null);
    const [effective, setEffective] = useState({}); // resolved: plan + overrides
    const [overrides, setOverrides] = useState(ws.feature_overrides || {});
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        Promise.all([
            api.get("/features/catalog"),
            // We don't have a per-tenant resolved endpoint; compute locally from catalog + plan + overrides
        ]).then(([cat]) => {
            setCatalog(cat.data);
            const planDefaults = cat.data.plan_matrix[ws.plan] || {};
            setEffective({ ...planDefaults, ...(ws.feature_overrides || {}) });
        }).catch(() => toast.error("Failed to load feature catalog"));
    }, [ws.id, ws.plan]);

    const isOverridden = (key) => Object.prototype.hasOwnProperty.call(overrides, key);
    const planDefault = (key) => (catalog?.plan_matrix?.[ws.plan] || {})[key];

    const toggle = (key) => {
        const currentEffective = effective[key];
        const newValue = !currentEffective;
        const newEffective = { ...effective, [key]: newValue };
        setEffective(newEffective);
        // If the new value matches plan default, clear the override; otherwise store it.
        const newOverrides = { ...overrides };
        if (newValue === planDefault(key)) delete newOverrides[key];
        else newOverrides[key] = newValue;
        setOverrides(newOverrides);
    };

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/workspaces/${ws.id}/features`, { overrides });
            toast.success("Feature overrides saved.");
            window.dispatchEvent(new CustomEvent("naukri:features-changed"));
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    if (!catalog) return <p className="text-sm text-[#A3A3A3]">Loading feature catalog…</p>;

    // Group by category.
    const grouped = catalog.features.reduce((acc, f) => {
        (acc[f.category] = acc[f.category] || []).push(f);
        return acc;
    }, {});

    return (
        <div className="space-y-6" data-testid="superadmin-features-tab">
            <div className="bg-cobalt/5 border border-cobalt/20 p-3 text-xs text-[#525252]">
                Plan: <b className="uppercase text-ink">{ws.plan}</b> · Toggle a feature to <b>override</b> its plan default. Overrides show a <span className="text-splash font-black">•</span> marker.
            </div>
            {Object.entries(grouped).map(([category, features]) => (
                <section key={category}>
                    <p className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] mb-2">{category}</p>
                    <div className="bg-white border border-[#E5E5E5]">
                        {features.map((f) => {
                            const enabled = effective[f.key];
                            const overridden = isOverridden(f.key);
                            const clearOverride = async () => {
                                try {
                                    await api.delete(`/workspaces/${ws.id}/features/${f.key}`);
                                    toast.success(`Override for "${f.label}" cleared.`);
                                    const newOverrides = { ...overrides };
                                    delete newOverrides[f.key];
                                    setOverrides(newOverrides);
                                    // Re-derive effective from plan defaults
                                    setEffective({ ...effective, [f.key]: planDefault(f.key) });
                                    window.dispatchEvent(new CustomEvent("naukri:features-changed"));
                                } catch (err) {
                                    toast.error(err?.response?.data?.detail || "Clear failed");
                                }
                            };
                            return (
                                <div key={f.key} className="flex items-center justify-between px-4 py-3 border-b border-[#F5F5F5] last:border-b-0">
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm text-ink flex items-center gap-1.5">
                                            {f.label}
                                            {overridden && <span data-testid={`feature-override-${f.key}`} className="text-splash text-lg leading-none">•</span>}
                                        </p>
                                        <p className="text-[10px] font-mono text-[#A3A3A3]">{f.key}</p>
                                    </div>
                                    {overridden && (
                                        <button
                                            type="button"
                                            onClick={clearOverride}
                                            data-testid={`feature-clear-${f.key}`}
                                            title="Reset to plan default"
                                            className="text-[10px] font-black uppercase tracking-wider text-[#A3A3A3] hover:text-splash mr-3"
                                        >
                                            reset
                                        </button>
                                    )}
                                    <button
                                        onClick={() => toggle(f.key)}
                                        data-testid={`feature-toggle-${f.key}`}
                                        className={`w-10 h-6 rounded-full transition-colors relative ${enabled ? "bg-cobalt" : "bg-[#E5E5E5]"}`}
                                        aria-pressed={enabled}
                                    >
                                        <span className={`block w-4 h-4 bg-white rounded-full absolute top-1 transition-all ${enabled ? "left-5" : "left-1"}`} />
                                    </button>
                                </div>
                            );
                        })}
                    </div>
                </section>
            ))}
            <div className="flex justify-end border-t border-[#F5F5F5] pt-4">
                <button onClick={save} disabled={saving} data-testid="features-save-btn"
                        className="btn-gradient px-5 py-2.5 text-xs font-black uppercase tracking-wider disabled:opacity-60">
                    {saving ? "Saving…" : "Save overrides"}
                </button>
            </div>
        </div>
    );
}

// ---------- LIMITS TAB ----------
function LimitsTab({ ws, onSaved }) {
    const [limits, setLimits] = useState({
        candidates: ws.limits?.candidates ?? "",
        jobs: ws.limits?.jobs ?? "",
        clients: ws.limits?.clients ?? "",
        ai_matches: ws.limits?.ai_matches ?? "",
        seats: ws.limits?.seats ?? "",
    });
    const [saving, setSaving] = useState(false);

    const save = async (e) => {
        e?.preventDefault();
        const body = {};
        for (const k of ["candidates", "jobs", "clients", "ai_matches", "seats"]) {
            const raw = limits[k];
            if (raw === "" || raw === null || raw === undefined) continue;
            const n = parseInt(raw, 10);
            if (Number.isNaN(n)) { toast.error(`${k} must be a number`); return; }
            body[k] = n;
        }
        if (!Object.keys(body).length) { toast.info("Nothing to save."); return; }
        setSaving(true);
        try {
            await api.patch(`/workspaces/${ws.id}/limits`, body);
            toast.success("Limits saved.");
            onSaved();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally { setSaving(false); }
    };

    return (
        <form onSubmit={save} data-testid="superadmin-limits-tab" className="space-y-4 max-w-3xl">
            <p className="text-xs text-[#525252]">
                Blank = use plan default. <span className="font-mono">-1</span> = unlimited. Positive = hard cap.
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {LIMIT_KEYS.map(({ key, label }) => (
                    <div key={key}>
                        <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">{label}</label>
                        <input type="number" value={limits[key]}
                               onChange={(e) => setLimits({ ...limits, [key]: e.target.value })}
                               data-testid={`limit-${key}`}
                               placeholder={fmtLimit(ws.limits?.[key])}
                               className="w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink font-mono" />
                    </div>
                ))}
            </div>
            <div className="flex justify-end border-t border-[#F5F5F5] pt-4">
                <button type="submit" disabled={saving} data-testid="limits-save-btn"
                        className="btn-gradient px-5 py-2.5 text-xs font-black uppercase tracking-wider disabled:opacity-60">
                    {saving ? "Saving…" : "Save limits"}
                </button>
            </div>
        </form>
    );
}

function Field({ label, value, onChange, placeholder, testid, mono }) {
    return (
        <div>
            <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1.5">{label}</label>
            <input value={value} onChange={(e) => onChange(e.target.value)}
                   data-testid={testid} placeholder={placeholder}
                   className={`w-full border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-ink ${mono ? "font-mono" : ""}`} />
        </div>
    );
}
